import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { isEmpty } from "lodash";

import useLanguageChange from "../../hooks/useLanguageChange";

import { CALL_BODY_SCREEN, DEVICE_TYPES } from "../../constants/Constants";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../../constants/FirebaseConstants";
import { CALL_DATA, DOCUMENT_VISIBILITY_STATE, GLOBAL_MEETING_DATA } from "../../constants/PublicVariables";
import { FLASH_BUTTON_STATUS_FOR, FLASH_STATE } from "../../constants/FlashConstants";
import { NOTIFICATION_TYPES } from "../../constants/DataTrackNotificationConstants";
import { EVENT_NAME } from "../../constants/GTagConstants";

import { setFlashState } from "../../reducers/flash-reducer";

import {
    dualStreamEnabled,
    isDeviceTypeAndroidBrowser,
    isDeviceTypeIOSBrowser,
    isFeatureFlagDisabled,
    isFeatureFlagEnabled,
} from "../../shared/utility";
import { isLocalUserStreaming, isStreamingUserVideoOrScreenShareOn, isvideoenabled } from "../Twilio/Room/roomFunction";
import { sendDataTrack } from "../../shared/dataTrackFunction";
import { LogFirebase } from "../../services/firestore";
import googleEvent from "../../services/googleEvent";
import flashService from "../../services/flashService";
import navigatorService from "../../services/navigatorService";
import { getLocalVideoTrack } from "../Twilio/Room/roomService";
import { isSilentObserverAvailable } from "../../shared/function";

const FlashButton = () => {
    const dispatch = useDispatch();

    const { currentCallBodyScreen, allowTakePhoto, expandView, isRemoteParticipantJoined, isRoomConnected, isHideVideo } = useSelector(
        (state) => state.commonReducer
    );
    const { streamingPartiIdentity, streamingDetails, screenSharingUsers, videoStreamingUsers, startLocalVideo, isOpponentUserScreenSharing } =
        useSelector((state) => state.twilioEvents);
    const { currentFlashState } = useSelector((state) => state.flash);

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    /* as added multiple dependancies with useEffect, function will call on each change on depandancy
    this will add event of visibility, means on each register of event, same function will call as many time as it will register
    to avoid this case, first deregister event before register it which will make function call once only */
    useEffect(() => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isRemoteParticipantJoined, startLocalVideo]);

    const handleVisibilityChange = () => {
        if (!isRemoteParticipantJoined) {
            return;
        }
        if (document.visibilityState) {
            if (flashService.isLocalUsersVideoStreamOn() && flashService.isSupportedMobileBrowser()) {
                checkForVisibilityHidden();
                checkForVisibilityVisible();
            }
        }
    };

    const checkForVisibilityHidden = () => {
        if (document.visibilityState === DOCUMENT_VISIBILITY_STATE.HIDDEN) {
            const localVideoTrack = getLocalVideoTrack();
            const mediaStreamTrack = localVideoTrack?.mediaStreamTrack;
            if (
                startLocalVideo &&
                isLocalUserStreaming() &&
                navigatorService.isMobileDevice() &&
                (isDeviceTypeAndroidBrowser() || isDeviceTypeIOSBrowser()) &&
                mediaStreamTrack
            ) {
                flashService.applyMediaStreamTrack(mediaStreamTrack, false, () => {
                    flashService.applyFlashChanges(true, FLASH_STATE.DISABLED, true);
                });
            } else {
                flashService.applyFlashChanges(true, FLASH_STATE.DISABLED, true);
            }
        }
    };

    const checkForVisibilityVisible = () => {
        if (document.visibilityState === DOCUMENT_VISIBILITY_STATE.VISIBLE && !isHideVideo) {
            /* timeout required else isFLashsupport gets track is in invalid state issue */
            setTimeout(() => {
                flashService.isFlashSupport((flashSupported) => {
                    flashService.applyFlashChanges(!flashSupported, flashSupported ? FLASH_STATE.ENABLED : FLASH_STATE.DISABLED, true);
                });
            }, 1500);
        }
    };

    //#region UI_functions

    /*
        - For mWeb and desktop do flash button will be disable while video on
        - For Mobile App flash button will be enable if video is on
    */
    const disabledButtonForWebMwebDualStream = () => {
        /* Check if user is Agent; dual stream is enabled; during dual stream, one participants view is expanded & streamining participant ID is exist, than check if device is supported of not */
        if (GLOBAL_MEETING_DATA.isAgent && dualStreamEnabled() && expandView && streamingPartiIdentity !== null) {
            const supportedDevices = [DEVICE_TYPES.ANDROID_BROWSER, DEVICE_TYPES.IOS_BROWSER, DEVICE_TYPES.DESKTOP];
            return supportedDevices.includes(streamingDetails.device_type);
        }

        return false;
    };

    const isButtonDisabled = () => {
        /* if dual stream enabled but not expand view than set flash disabled and return button state as disabled */
        if (dualStreamEnabled() && !expandView) {
            if (currentFlashState !== FLASH_STATE.DISABLED) {
                dispatch(setFlashState(FLASH_STATE.DISABLED));
            }
            return true;
        }

        /* if remote partcipant joined and started local video than disable check for flash availability and state */
        return !(isRoomConnected && isRemoteParticipantJoined && isvideoenabled());
    };

    const memoizedFeatureDisabledState = () => {
        if (!isRemoteParticipantJoined) {
            return true;
        }

        if (isButtonDisabled() || !expandView) {
            return true;
        }

        if (currentCallBodyScreen !== CALL_BODY_SCREEN.NOTIFY_SCREEN && !CALL_DATA.disabledFlash) {
            if (dualStreamEnabled()) {
                return isStreamingUserVideoOrScreenShareOn();
            } else {
                return isOpponentUserScreenSharing;
            }
        }

        return true;
    };

    /* for local flash button, send true
        for remote flash button send false */
    const isFeatureDisabled = (forLocal) => {
        if (forLocal && !startLocalVideo) {
            return true;
        }

        if (!forLocal && disabledButtonForWebMwebDualStream()) {
            return true;
        }

        return memoizedFeatureDisabledState();
    };

    /* FlashButton Status function for button status green or white if button enable */
    /* old function name 'checkFlashButtonStatus' */
    const getCurrentFlashState = (stateFor, checkForLocalOrRemote = null) => {
        if (allowTakePhoto && expandView) {
            if (
                (stateFor === FLASH_BUTTON_STATUS_FOR.FLASH_OFF &&
                    (+currentFlashState === FLASH_STATE.OFF || +currentFlashState === FLASH_STATE.ENABLED)) ||
                (stateFor === FLASH_BUTTON_STATUS_FOR.FLASH_ON && +currentFlashState === FLASH_STATE.ON)
            ) {
                if (checkForLocalOrRemote !== null) {
                    // Check for adding active class on flash button, if feature is disable, than icon not to be active
                    return !isFeatureDisabled(checkForLocalOrRemote);
                } else {
                    return true;
                }
            }
        }

        return false;
    };

    /* for local flash button, send true
        for remote flash button send false */
    const flashIcon = (forLocal) => {
        let iconClass = "";
        const isFeatureDisable = isFeatureDisabled(forLocal);
        if (isFeatureDisable) {
            iconClass = "for-icon icon-flash-off";
        } else if (!isFeatureDisable && getCurrentFlashState(FLASH_BUTTON_STATUS_FOR.FLASH_ON)) {
            iconClass = "for-icon icon-flash-on";
        } else if (!isFeatureDisable && getCurrentFlashState(FLASH_BUTTON_STATUS_FOR.FLASH_OFF)) {
            iconClass = "for-icon icon-flash-off";
        }

        return (
            <span className='action-button-icon'>
                <span className={iconClass}></span>
            </span>
        );
    };

    /* flash off button text to shows */
    /* old name flashOffButttonText */
    const isFlashOFF = () => {
        if (+currentFlashState === FLASH_STATE.ON) {
            if (dualStreamEnabled()) {
                return !isStreamingUserVideoOrScreenShareOn();
            } else {
                return isEmpty(screenSharingUsers);
            }
        }

        return false;
    };

    /* flash On button text to shows*/
    /* old name flashOnButttonText */
    const isFlashON = () => {
        const flashStatesArray = [FLASH_STATE.OFF, FLASH_STATE.ENABLED, FLASH_STATE.DISABLED];
        if ((!CALL_DATA.disabledFlash || flashStatesArray.includes(+currentFlashState)) && +currentFlashState !== FLASH_STATE.ON) {
            return true;
        }

        if (dualStreamEnabled()) {
            return isStreamingUserVideoOrScreenShareOn();
        } else {
            return screenSharingUsers?.length > 0;
        }
    };

    /* for local flash button, send true
        for remote flash button send false */
    const flashText = (forLocal) => {
        if (isFeatureDisabled(forLocal)) {
            return <span className='for-text'>{localisedValues?.flash_on}</span>;
        }

        if (forLocal) {
            if (+currentFlashState === FLASH_STATE.ON && isEmpty(screenSharingUsers)) {
                return <span className='for-text'>{localisedValues?.flash_off}</span>;
            }

            const flashStatesArray = [FLASH_STATE.OFF, FLASH_STATE.ENABLED, FLASH_STATE.DISABLED];
            if (
                ((startLocalVideo || !CALL_DATA.disabledFlash || flashStatesArray.includes(+currentFlashState)) &&
                    +currentFlashState !== FLASH_STATE.ON) ||
                screenSharingUsers?.length > 0
            ) {
                return <span className='for-text'>{localisedValues?.flash_on}</span>;
            }
        } else {
            if (isFlashON()) {
                return <span className='for-text'>{localisedValues?.flash_on}</span>;
            }

            if (isFlashOFF()) {
                return <span className='for-text'>{localisedValues?.flash_off}</span>;
            }
        }

        return <></>;
    };

    /* tooltip is for remote flash only */
    const flashTooltip = useMemo(() => {
        let text = "";
        const remoteDataTrackSubscribed = true;

        if (GLOBAL_MEETING_DATA.isAgent && isFeatureFlagEnabled("SHOWMEDUALSTREAM")) {
            if (!expandView && isvideoenabled() && remoteDataTrackSubscribed && isFeatureDisabled(false)) {
                /* as tooltip is for remote button, sending false as paramater */
                text = localisedValues?.pin_a_video_tile_above_to_use_this_feature;
            }

            if (expandView && remoteDataTrackSubscribed && isFeatureDisabled(false)) {
                /* as tooltip is for remote button, sending false as paramater */
                text = localisedValues?.remote_flashlight_not_supported_on_guest_device_or_not_ready;
            }
        }

        if (isFeatureFlagDisabled("SHOWMEDUALSTREAM") && isFeatureDisabled(false) && remoteDataTrackSubscribed) {
            /* as tooltip is for remote button, sending false as paramater */
            text = localisedValues?.remote_flashlight_not_supported_on_guest_device_or_not_ready;
        }

        if (text) {
            return (
                <div className='custom-tooltip-content text-nowrap'>
                    <div className='custom-tooltip-in'>
                        <p>{text}</p>
                    </div>
                </div>
            );
        }

        return <></>;
    }, [screenSharingUsers, videoStreamingUsers, expandView, localisedValues, currentFlashState]);
    //#endregion

    const localFlashClickHandler = () => {
        flashService.handleFlashOnOff();
    };

    const remoteFlashClickHandler = () => {
        const msg = {
            notification_type: NOTIFICATION_TYPES.FLASHLIGHT.FROM_REMOTE,
            identity: CALL_DATA.localIdentity,
        };
        if (+currentFlashState === FLASH_STATE.ON) {
            googleEvent.logEvent(EVENT_NAME.REMOTE_FLASH_ON);
            msg.isEnabled = false;
            dispatch(setFlashState(FLASH_STATE.OFF));
        } else if (+currentFlashState === FLASH_STATE.OFF) {
            googleEvent.logEvent(EVENT_NAME.REMOTE_FLASH_OFF);
            msg.isEnabled = true;
            dispatch(setFlashState(FLASH_STATE.ON));
        } else if (+currentFlashState === FLASH_STATE.ENABLED) {
            msg.isEnabled = true;
            dispatch(setFlashState(FLASH_STATE.ON));
        }
        sendDataTrack(msg);
        LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.SendMessage, msg);
    };

    return (
        <>
            {/* for local user */}
            {!isSilentObserverAvailable() && flashService.checkLocalFlashFeatureAvailable() && (
                <div className='local action-button'>
                    <div className='action-button-in custom-tooltip top-tooltip center-tooltip tooltip-auto-width'>
                        <button
                            className={clsx(
                                "call-action-btn",
                                getCurrentFlashState(FLASH_BUTTON_STATUS_FOR.FLASH_ON, true) && "active"
                            )} /* as this is local flash button, sending true as parameter */
                            onClick={() => localFlashClickHandler()}
                            disabled={isFeatureDisabled(true)} /* as this is local flash button, sending true as parameter */
                            data-testid='flashButtonGuest'
                        >
                            {flashIcon(true)} {/* as this is local flash button, sending true as parameter */}
                            {flashText(true)} {/* as this is local flash button, sending true as parameter */}
                        </button>
                    </div>
                </div>
            )}
            {/* for remote user */}
            {GLOBAL_MEETING_DATA.isAgent && isFeatureFlagEnabled("SHOWMEREMOTEFLASH") && (
                <div className='remote action-button'>
                    <div className='action-button-in custom-tooltip top-tooltip center-tooltip tooltip-auto-width'>
                        <button
                            className={clsx(
                                "call-action-btn",
                                getCurrentFlashState(FLASH_BUTTON_STATUS_FOR.FLASH_ON, false) && "active"
                            )} /* as this is remote flash button, sending false as parameter */
                            onClick={() => remoteFlashClickHandler()}
                            disabled={isFeatureDisabled(false)} /* as this is remote flash button, sending false as parameter */
                            data-testid='flashButton'
                        >
                            {flashIcon(false)} {/* as this is remote flash button, sending false as parameter */}
                            {flashText(false)} {/* as this is remote flash button, sending false as parameter */}
                        </button>
                        {flashTooltip}
                    </div>
                </div>
            )}
        </>
    );
};

export default FlashButton;
